// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson

import './styles.css';
import 'leaflet/dist/leaflet.css';

import { loadAll, type Bundle } from './data';
import { num, pct1, asAtLabel, escapeHtml } from './format';
import { initTooltip } from './components/tooltip';
import { openModal, showGlossary } from './components/overlay';
import { GLOSSARY } from './glossary';
import { renderMap } from './views/map';
import { renderGrowth } from './views/growth';
import { renderEras } from './views/eras';
import { renderMoves } from './views/moves';
import { renderLadder } from './views/ladder';
import { renderIntake } from './views/intake';
import { renderAgencies } from './views/agencies';

type ViewId = 'map' | 'growth' | 'eras' | 'moves' | 'ladder' | 'intake' | 'agencies';

const VIEWS: { id: ViewId; label: string }[] = [
  { id: 'map', label: 'Map' },
  { id: 'growth', label: 'Growth' },
  { id: 'eras', label: 'Eras' },
  { id: 'moves', label: 'Moves' },
  { id: 'ladder', label: 'Ladder' },
  { id: 'intake', label: 'Intake' },
  { id: 'agencies', label: 'Agencies' },
];

const app = document.getElementById('app')!;
let bundle: Bundle | null = null;
let current: ViewId = 'map';
let pinnedRegion: string | null = null;
let teardown: (() => void) | null = null;

function shell(): void {
  app.innerHTML = `
    <header class="site-header">
      <div class="header-inner">
        <div class="brand">
          <h1>Public Service</h1>
          <span class="brand-sub">Australia&rsquo;s federal workforce, 2006&ndash;2025</span>
        </div>
        <div class="header-spacer"></div>
        <div class="header-actions">
          <button type="button" class="icon-btn" id="about-btn" aria-label="About this site and its data">About</button>
        </div>
      </div>
    </header>
    <nav class="site-nav" aria-label="Views">
      <div class="nav-inner" role="tablist">
        ${VIEWS.map((v) => `<button type="button" class="nav-tab" role="tab" data-view="${v.id}"
          aria-selected="${v.id === current}" id="tab-${v.id}">${v.label}</button>`).join('')}
      </div>
    </nav>
    <main class="main-content" id="view-root" role="tabpanel">
      <div class="loading">Loading the APS Employment Database…</div>
    </main>
    <footer class="site-footer">
      <div class="footer-inner">
        <div class="footer-src" id="footer-src"></div>
        <div>Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> &middot;
          <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></div>
      </div>
    </footer>
    <svg width="0" height="0" style="position:absolute" aria-hidden="true" focusable="false">
      <defs>
        <pattern id="hatch" patternUnits="userSpaceOnUse" width="4" height="4" patternTransform="rotate(45)">
          <rect width="4" height="4" fill="#eef2f6"></rect>
          <line x1="0" y1="0" x2="0" y2="4" stroke="#9bacbd" stroke-width="1.4"></line>
        </pattern>
      </defs>
    </svg>`;

  app.querySelectorAll<HTMLButtonElement>('[data-view]').forEach((btn) => {
    btn.addEventListener('click', () => go(btn.dataset.view as ViewId));
  });
  document.getElementById('about-btn')!.addEventListener('click', showAbout);
}

function go(id: ViewId): void {
  current = id;
  location.hash = `#/${id}`;
  app.querySelectorAll<HTMLButtonElement>('[data-view]').forEach((b) =>
    b.setAttribute('aria-selected', String(b.dataset.view === id)));
  render();
}

function render(): void {
  if (!bundle) return;
  if (teardown) { teardown(); teardown = null; }
  const root = document.getElementById('view-root')!;
  root.innerHTML = '';
  root.setAttribute('aria-labelledby', `tab-${current}`);
  try {
    switch (current) {
      case 'map':
        teardown = renderMap(root, bundle, (name) => { pinnedRegion = name; go('growth'); });
        break;
      case 'growth': renderGrowth(root, bundle, pinnedRegion); break;
      case 'eras': renderEras(root, bundle); break;
      case 'moves': renderMoves(root, bundle); break;
      case 'ladder': renderLadder(root, bundle); break;
      case 'intake': renderIntake(root, bundle); break;
      case 'agencies': renderAgencies(root, bundle); break;
    }
  } catch (err) {
    root.innerHTML = `<div class="error-state">
      <p>Something went wrong drawing this view.</p>
      <p style="font-size:var(--font-size-sm)">${escapeHtml(String(err))}</p>
      <button type="button" class="icon-btn" style="background:var(--accent-primary);margin-top:var(--space-md)"
        onclick="location.reload()">Reload</button></div>`;
  }
  window.scrollTo({ top: 0 });
}

function showAbout(): void {
  const b = bundle;
  if (!b) return;
  const gatesOk = b.meta.gates.filter((g) => g.ok).length;
  openModal(`
    <h3 id="modal-title">About this site</h3>
    <p>This site shows who works for the Australian Government, where, and at what rank — using the
      <span class="glossary-link" data-term="apsed">APS Employment Database</span>, which the Australian Public
      Service Commission publishes twice a year.</p>

    <h4>What the data covers</h4>
    <p>Every employee of the <span class="glossary-link" data-term="aps">Australian Public Service</span> at
      ${escapeHtml(asAtLabel(b.meta.asAt))} — <strong>${num(b.meta.total)}</strong> people across
      ${b.map.regions.length} regions and ${b.agencies.fingerprints.length} employers, with history back to
      ${b.meta.years[0]}.</p>
    <p>It does <strong>not</strong> include the Australian Defence Force, parliamentarians or their staff,
      contractors and labour hire, or state and local government employees. Figures are
      <span class="glossary-link" data-term="headcount">headcount</span>, not full-time equivalent.</p>

    <h4>How to read it honestly</h4>
    <ul>
      <li>A figure the Commission <span class="glossary-link" data-term="suppressed">confidentialised</span>
        appears as “withheld” and is drawn hatched. It is <em>not</em> zero — real people sit behind it, and
        totals will therefore exceed the sum of the visible parts.</li>
      <li>Men and women do not add to the total, because
        <span class="glossary-link" data-term="genderX">gender X</span> employees are included in totals but
        are not published as a separate line.</li>
      <li>Agencies merge, split and get renamed through
        <span class="glossary-link" data-term="mog">machinery of government</span> changes, so tracking a single
        agency across twenty years is unreliable. The long time series on this site are therefore drawn at
        classification and location level, where they <em>are</em> comparable.</li>
      <li>Part of the 2022–25 increase is the in-sourcing of work previously done by contractors and labour
        hire, following the government's Audit of Employment — so it is not all new work for government. That
        is why this site is about <em>where</em> the roles landed rather than about the headline growth number.</li>
      <li>Employees are counted where their agency reports them, which need not be where they live.</li>
    </ul>

    <h4>How it is built</h4>
    <p>A pipeline downloads the release workbook, parses all 95 tables, and checks the result against
      <strong>${gatesOk} of ${b.meta.gatesTotal}</strong> reconciliation gates before anything is published —
      including three exact identities: the ${b.map.regions.length} regions, the nine states, and the thirteen
      classifications each sum to precisely ${num(b.meta.total)}.</p>
    <p>Updated twice a year, following the Commission's own release cycle. Current release:
      ${escapeHtml(b.meta.release)}.</p>

    <h4>Sources and licence</h4>
    <ul>
      <li>Australian Public Service Commission, <em>APS Employment Database (APSED)</em> release tables, via
        <a href="https://data.gov.au/data/dataset/aps-employment-data" target="_blank" rel="noopener">data.gov.au</a>. Licensed CC BY.</li>
      <li>Australian Bureau of Statistics, <em>Australian Statistical Geography Standard (ASGS) Edition 3, 2021</em>
        — SA4 boundaries. Licensed CC BY 4.0.</li>
      <li>Basemap &copy; OpenStreetMap contributors, &copy; CARTO.</li>
    </ul>
    <p style="font-size:var(--font-size-xs)">This is an independent site and is not affiliated with, or endorsed
      by, the Australian Public Service Commission or the Australian Government.</p>
  `);
}

function wireGlossary(): void {
  document.addEventListener('click', (e) => {
    const link = (e.target as Element).closest<HTMLElement>('.glossary-link');
    if (!link) return;
    const key = link.dataset.term;
    const entry = key ? GLOSSARY[key] : undefined;
    if (!entry) return;
    e.preventDefault();
    e.stopPropagation();
    showGlossary(link, entry.term, entry.definition);
  });
}

function footer(b: Bundle): void {
  document.getElementById('footer-src')!.innerHTML =
    `Source: Australian Public Service Commission, <em>APS Employment Database</em>, ${escapeHtml(asAtLabel(b.meta.asAt))} `
    + `(CC BY, via data.gov.au). Boundaries: ABS ASGS 2021 (CC BY 4.0). `
    + `${num(b.meta.total)} employees · ${b.meta.gatesPassed}/${b.meta.gatesTotal} data checks passing. `
    + `<button type="button" class="icon-btn" id="footer-about" style="background:var(--bg-elevated);color:var(--accent-secondary);border-color:var(--border-default);min-height:28px;margin-left:6px">Methodology</button>`;
  document.getElementById('footer-about')!.addEventListener('click', showAbout);
}

async function boot(): Promise<void> {
  shell();
  initTooltip();
  wireGlossary();
  const hash = location.hash.replace('#/', '') as ViewId;
  if (VIEWS.some((v) => v.id === hash)) current = hash;
  app.querySelectorAll<HTMLButtonElement>('[data-view]').forEach((b) =>
    b.setAttribute('aria-selected', String(b.dataset.view === current)));

  const controller = new AbortController();
  try {
    bundle = await loadAll(controller.signal);
    footer(bundle);
    render();
  } catch (err) {
    document.getElementById('view-root')!.innerHTML = `
      <div class="error-state">
        <p>The data could not be loaded.</p>
        <p style="font-size:var(--font-size-sm)">${escapeHtml(String(err))}</p>
        <button type="button" class="icon-btn" style="background:var(--accent-primary);margin-top:var(--space-md)"
          onclick="location.reload()">Try again</button>
      </div>`;
  }
  window.addEventListener('hashchange', () => {
    const h = location.hash.replace('#/', '') as ViewId;
    if (VIEWS.some((v) => v.id === h) && h !== current) go(h);
  });
  void pct1;
}

void boot();
